import styled, { css } from 'styled-components';
import { CellVariant } from '../diffHighlight';

/**
 * Wraps one section's two version cards. It defines the shared row tracks; each card is a CSS
 * subgrid spanning them, so corresponding rows in the two cards stay vertically aligned even though
 * they are separate panels. `grid-template-rows` (the row count) is set inline per section.
 */
export const SectionOuter = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 24px;
  width: 100%;
  margin-top: 16px;
`;

/** A single version's panel: a white card with a header and an inset bordered table. */
export const SectionCard = styled.div`
  grid-row: 1 / -1;
  display: grid;
  grid-template-rows: subgrid;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  padding: 0 24px 20px;

  /* Bordered table: every field row (all children except the header) gets side + bottom borders. */
  & > *:not(:first-child) {
    border-left: 1px solid ${(props) => props.theme.colors.elementsInputBorder};
    border-right: 1px solid ${(props) => props.theme.colors.elementsInputBorder};
    border-bottom: 1px solid ${(props) => props.theme.colors.elementsInputBorder};
  }

  /* Top of the table (first field row, right after the header). */
  & > *:nth-child(2) {
    border-top: 1px solid ${(props) => props.theme.colors.elementsInputBorder};
  }
`;

/** Section header shown at the top of each card. */
export const CardHeader = styled.div`
  ${(props) => props.theme.typography.headerCaptionSemiBold}
  padding: 28px 0 16px;
`;

const variantBackground = css<{ $variant?: CellVariant }>`
  background-color: ${(props) =>
    props.$variant === 'reference'
      ? props.theme.colors.statusLabelRedBg
      : props.$variant === 'compared'
      ? props.theme.colors.secondaryLightGreen
      : props.$variant === 'empty'
      ? props.theme.colors.elementsInputBorder
      : '#fff'};
`;

/** A key/value field cell: fixed label column + value, split by an internal divider. */
export const ValueCell = styled.div<{ $variant?: CellVariant }>`
  display: grid;
  grid-template-columns: 250px 1fr;
  ${variantBackground}
`;

/** A block field cell (files / images) spanning the cell width. */
export const BlockCell = styled.div<{ $variant?: CellVariant }>`
  padding: 0.5rem 1rem;
  ${variantBackground}
`;

export const LabelText = styled.span`
  ${(props) => props.theme.typography.contentTableBodyXs}
  padding: 0.5rem 1rem;
`;

export const ValueText = styled.div`
  ${(props) => props.theme.typography.contentTableBodyXs}
  padding: 0.5rem 1rem;
  border-left: 1px solid ${(props) => props.theme.colors.elementsInputBorder};
  white-space: pre-wrap;
  word-break: break-word;

  & > p,
  & > ul {
    margin: 0;
  }
`;
